import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Link } from '@inertiajs/react'
import { Award, Save, X } from 'lucide-react'

interface Community {
  id: number
  name: string
}

interface ConditionType {
  value: string
  label: string
}

interface BadgeFormData {
  name: string
  description: string
  emoji: string
  category: string
  community_id: string
  points_reward: string
  condition_type: string
  condition_value: Record<string, unknown>
}

interface BadgeFormProps {
  data: BadgeFormData
  setData: <K extends keyof BadgeFormData>(key: K, value: BadgeFormData[K]) => void
  errors: Partial<Record<keyof BadgeFormData, string>>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  categories: string[]
  communities: Community[]
  conditionTypes: ConditionType[]
  submitLabel: string
  cancelHref: string
}

const COMMON_EMOJIS = [
  '🏆', '🎖️', '🥇', '🥈', '🥉', '⭐', '🌟', '💫',
  '🔥', '💪', '🎯', '🚀', '💎', '👑', '🏅', '🎉',
  '📚', '💬', '❤️', '🙌', '✨', '🌈', '🎁', '🎊'
]

function getCategoryLabel(category: string): string {
  switch (category) {
    case 'performance': return 'Performance (API Gamification)'
    case 'community': return 'Communauté (actions sociales)'
    case 'recognition': return 'Reconnaissance (attribution manuelle)'
    case 'seniority': return 'Ancienneté'
    default: return category
  }
}

export default function BadgeForm({
  data,
  setData,
  errors,
  processing,
  onSubmit,
  categories,
  communities,
  conditionTypes,
  submitLabel,
  cancelHref
}: BadgeFormProps) {
  return (
    <form onSubmit={onSubmit}>
      <div className="grid md:grid-cols-3 gap-6">
        {/* Main Info */}
        <Card className="p-6 md:col-span-2">
          <h3 className="text-lg font-display font-bold text-text-dark mb-4 flex items-center gap-2">
            <Award size={20} className="text-tessan-primary" />
            Informations du badge
          </h3>

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-text-dark mb-1">
                Nom du badge *
              </label>
              <Input
                id="name"
                type="text"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                placeholder="Ex: Super Contributeur"
                required
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-text-dark mb-1">
                Description
              </label>
              <Textarea
                id="description"
                value={data.description}
                onChange={(e) => setData('description', e.target.value)}
                placeholder="Décrivez comment obtenir ce badge..."
                rows={3}
              />
              {errors.description && (
                <p className="text-sm text-red-600 mt-1">{errors.description}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-text-dark mb-1">
                  Catégorie *
                </label>
                <Select
                  id="category"
                  value={data.category}
                  onChange={(e) => setData('category', e.target.value)}
                  required
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {getCategoryLabel(category)}
                    </option>
                  ))}
                </Select>
                {errors.category && (
                  <p className="text-sm text-red-600 mt-1">{errors.category}</p>
                )}
              </div>

              <div>
                <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
                  Communauté
                </label>
                <Select
                  id="community_id"
                  value={data.community_id}
                  onChange={(e) => setData('community_id', e.target.value)}
                >
                  <option value="">Badge global (toutes communautés)</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="condition_type" className="block text-sm font-medium text-text-dark mb-1">
                  Condition d'obtention
                </label>
                <Select
                  id="condition_type"
                  value={data.condition_type}
                  onChange={(e) => setData('condition_type', e.target.value)}
                >
                  {conditionTypes.map((ct) => (
                    <option key={ct.value} value={ct.value}>
                      {ct.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label htmlFor="points_reward" className="block text-sm font-medium text-text-dark mb-1">
                  Points bonus
                </label>
                <Input
                  id="points_reward"
                  type="number"
                  min="0"
                  value={data.points_reward}
                  onChange={(e) => setData('points_reward', e.target.value)}
                  placeholder="0"
                />
                <p className="text-xs text-text-gray mt-1">
                  Points attribués quand le badge est obtenu
                </p>
              </div>
            </div>

            {data.condition_type && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <label htmlFor="condition_threshold" className="block text-sm font-medium text-text-dark mb-1">
                  Seuil requis
                </label>
                <Input
                  id="condition_threshold"
                  type="number"
                  min="1"
                  value={(data.condition_value?.threshold as number) || ''}
                  onChange={(e) => setData('condition_value', { threshold: parseInt(e.target.value) || 0 })}
                  placeholder="Ex: 10"
                />
                <p className="text-xs text-text-gray mt-1">
                  Valeur minimale pour débloquer automatiquement ce badge
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Emoji Selection */}
        <Card className="p-6">
          <h3 className="text-lg font-display font-bold text-text-dark mb-4">
            Emoji du badge *
          </h3>

          {/* Preview */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-24 h-24 rounded-2xl bg-tessan-cream flex items-center justify-center text-5xl mb-2">
              {data.emoji || '?'}
            </div>
            <p className="text-sm text-text-gray">Aperçu</p>
          </div>

          {/* Manual Input */}
          <div className="mb-4">
            <label htmlFor="emoji" className="block text-sm font-medium text-text-dark mb-1">
              Emoji personnalisé
            </label>
            <Input
              id="emoji"
              type="text"
              value={data.emoji}
              onChange={(e) => setData('emoji', e.target.value)}
              placeholder="🏆"
              maxLength={4}
              className="text-center text-2xl"
            />
            {errors.emoji && (
              <p className="text-sm text-red-600 mt-1">{errors.emoji}</p>
            )}
          </div>

          {/* Quick Select */}
          <div>
            <p className="text-sm font-medium text-text-dark mb-2">Sélection rapide</p>
            <div className="grid grid-cols-6 gap-2">
              {COMMON_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setData('emoji', emoji)}
                  className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-colors ${
                    data.emoji === emoji
                      ? 'bg-tessan-primary text-white'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4 mt-6">
        <Link href={cancelHref}>
          <Button type="button" variant="secondary" className="flex items-center gap-2">
            <X size={16} />
            Annuler
          </Button>
        </Link>
        <Button type="submit" disabled={processing} className="flex items-center gap-2">
          <Save size={16} />
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
